import { z } from 'zod';
export const customer = z.object({
    id: z.number().int().positive(),
    name: z.string(),
    company: z.string(),
    email: z.string().email(),
    phone: z.string().nullable().optional(),
    website: z.string().nullable().optional(),
    // Strukturierte Adresse
    street: z.string().nullable().optional(),
    houseNumber: z.string().nullable().optional(),
    zip: z.string().nullable().optional(),
    city: z.string().nullable().optional(),
    // Legacy single-field
    address: z.string().nullable().optional(),
    type: z.string().nullable().optional(),
    notes: z.string().nullable().optional(),
    // Branding-Overrides pro Kunde (Phase B) — leer = globale Büro-Marke greift.
    mailDomain: z.string().nullable().optional(),
    logoPath: z.string().nullable().optional(),
    brandColor: z.string().nullable().optional(),
    fontFamily: z.string().nullable().optional(),
    documentTemplate: z.string().nullable().optional(),
    customFooter: z.string().nullable().optional(),
    // Geo
    lat: z.number().nullable().optional(),
    lng: z.number().nullable().optional(),
    geocodedAt: z.string().datetime().nullable().optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
const websiteSchema = z
    .string()
    .trim()
    .max(300)
    .refine((v) => !v || /^https?:\/\//i.test(v) || /^[\w.-]+\.[a-z]{2,}/i.test(v), {
    message: 'Bitte eine gültige Web-Adresse eingeben',
})
    .nullable()
    .optional();
const zipSchema = z
    .string()
    .trim()
    .regex(/^\d{4,5}$/, { message: 'PLZ muss 4–5 Ziffern haben' })
    .nullable()
    .optional()
    .or(z.literal(''));
export const createCustomerRequest = customer
    .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
    lat: true,
    lng: true,
    geocodedAt: true,
    // Logo wird per Upload-Endpoint gesetzt, nicht über das Formular-DTO.
    logoPath: true,
})
    .extend({
    name: z.string().min(2),
    company: z.string().min(2),
    website: websiteSchema,
    zip: zipSchema,
    notes: z.string().max(5000).nullable().optional(),
    mailDomain: z.string().trim().max(200).nullable().optional(),
    brandColor: z.string().trim().max(20).nullable().optional(),
    fontFamily: z.string().trim().max(120).nullable().optional(),
    documentTemplate: z.string().trim().max(120).nullable().optional(),
    customFooter: z.string().trim().max(2000).nullable().optional(),
});
export const updateCustomerRequest = createCustomerRequest.partial();
/**
 * Spalten, nach denen die Kundenliste sortiert werden darf.
 *
 * Bewusst eine Allowlist und im gemeinsamen Paket: Frontend und Backend müssen
 * sich über dieselben Namen einig sein, sonst zeigt ein Spaltenkopf eine
 * Sortierung an, die der Server stillschweigend auf den Default zurückfallen
 * lässt.
 */
export const CUSTOMER_SORT_FIELDS = ['name', 'company', 'email', 'type', 'createdAt'];
/** Query der Kundenliste. `search` = Teiltreffer, `type` = exakter Wert. */
export const customerListQuery = z.object({
    search: z
        .string()
        .trim()
        .max(200)
        .optional()
        .transform((v) => (v ? v : undefined)),
    type: z
        .string()
        .trim()
        .max(100)
        .optional()
        .transform((v) => (v ? v : undefined)),
    /** `feld` = aufsteigend, `-feld` = absteigend (gleiche Konvention wie /projects). */
    sort: z
        .string()
        .trim()
        .max(40)
        .optional()
        .transform((v) => (v ? v : undefined)),
    limit: z.coerce.number().int().positive().max(200).default(50),
    offset: z.coerce.number().int().nonnegative().default(0),
});
/**
 * Aus den strukturierten Feldern eine kompakte Adresszeile bauen.
 * Wird sowohl Backend- als auch Frontend-seitig genutzt.
 */
export function composeAddress(parts) {
    const line1 = [parts.street, parts.houseNumber].filter(Boolean).join(' ').trim();
    const line2 = [parts.zip, parts.city].filter(Boolean).join(' ').trim();
    return [line1, line2].filter(Boolean).join(', ');
}
